import {FloatingUIElementProps} from '@app/helpers/react';
import {
  autoUpdate,
  flip,
  offset,
  Placement,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import {ReactNode, useState} from 'react';
import styled, {keyframes} from 'styled-components';

import Luiv3Constants from '../lui3/constants';
import FloatingPortal from './FloatingPortal';
import {colorVar} from './Theme/colors';

type Style = 'contrast' | 'error' | 'primary' | 'light';

interface TooltipProps {
  text?: ReactNode;
  variant?: Style;
  placement?: Placement;
  delay?: boolean;
  disabled?: boolean;
  interactive?: boolean;
  wordWrapBreakWord?: boolean;
  className?: string;
  maxWidth?: string;
  offset?: number;
  children?:
    | ReactNode
    | ((props: FloatingUIElementProps) => ReactNode)
    | undefined;
}

const delayedEnter = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Popper = styled.span<{maxWidth?: string}>`
  z-index: 1050;
  border-radius: 0.75rem;
  font-weight: var(--theme-font-weight-control);
  padding: 0.4375rem 0.6875rem;
  max-width: ${({maxWidth}) => maxWidth ?? '12.5rem'};
  text-align: left;
  line-height: 1.125rem;

  &[data-variant='contrast'] {
    background-color: var(--theme-contrast-color);
    color: var(--theme-on-contrast-color);
  }

  &[data-variant='primary'] {
    background-color: var(--theme-primary-color);
    color: var(--theme-on-primary-color);
  }

  &[data-variant='error'] {
    background-color: ${colorVar('error')};
    color: ${colorVar('onError')};
  }

  &[data-variant='light'] {
    background-color: ${Luiv3Constants.baseColor['gray-0']};
    color: ${Luiv3Constants.baseColor['gray-1000']};
    box-shadow: 0 0.25rem 1rem rgba(3, 6, 12, 0.12);

    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
    letter-spacing: 0.00875rem;
  }

  &[data-delay] {
    animation: ${delayedEnter} 2000ms step-end;
  }

  &[data-word-wrap='break-word'] {
    word-wrap: break-word;
  }
`;

const Tooltip = ({
  text,
  variant = 'contrast',
  placement = 'top',
  delay,
  children,
  disabled,
  className,
  maxWidth,
  wordWrapBreakWord,
  interactive,
  offset: offsetValue = 4,
}: TooltipProps) => {
  const [open, setOpen] = useState(false);

  const {x, y, refs, strategy, context} = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [offset(offsetValue), flip({padding: 8}), shift({padding: 8})],
  });

  const {getReferenceProps, getFloatingProps} = useInteractions([
    useHover(context, interactive ? {handleClose: safePolygon()} : undefined),
    useDismiss(context, {referencePress: true}),
  ]);

  const props = getReferenceProps();

  return (
    <>
      {typeof children === 'function' ? (
        children({...props, ref: refs.setReference})
      ) : (
        <div className={className} {...props} ref={refs.setReference}>
          {children}
        </div>
      )}
      <FloatingPortal>
        {open && !disabled && text && (
          <Popper
            {...getFloatingProps({
              style: {
                position: strategy,
                top: y ?? '',
                left: x ?? '',
              },
            })}
            maxWidth={maxWidth}
            ref={refs.setFloating}
            data-variant={variant}
            data-delay={delay || undefined}
            data-word-wrap={wordWrapBreakWord ? 'break-word' : undefined}
          >
            {text}
          </Popper>
        )}
      </FloatingPortal>
    </>
  );
};

export default Tooltip;
